import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { API } from 'api';
import {
  AlertCriteria,
  AlertMetadata,
  WeatherAlert,
} from 'features/weatherAlerts/weatherAlerts.types';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { getPushTokenFromLocalStorage } from 'state/localStorage';
import {
  WEATHER_ALERT_CACHE_KEY,
  WEATHER_ALERT_CACHE_TTL,
  WEATHER_ALERTS_LIST_CACHE_KEY,
} from '../../weatherAlerts.constants';

type UseWeatherAlertProps = {
  alertId?: number;
};

export type SaveAlertData = {
  id?: number;
  isEnabled?: boolean;
  stationId: number;
  criteria: AlertCriteria;
  metadata?: AlertMetadata;
};

type UseWeatherAlert = Omit<UseQueryResult<WeatherAlert, Error>, 'data'> & {
  alert?: WeatherAlert;
  saveAlert: (alertData: SaveAlertData) => Promise<WeatherAlert>;
  deleteAlert: () => Promise<void>;
};

type ServerMutationAlertData = SaveAlertData & {
  pushToken: string | null;
};

type DeleteAlertData = {
  id: number;
  pushToken: string | null;
};

const fetchAlert = async (alertId?: number): Promise<WeatherAlert> => {
  const response = await API.get<WeatherAlert>(`weather/alerts/${alertId}`);
  return response.data;
};

const createAlertMutation = async (
  data: ServerMutationAlertData,
): Promise<WeatherAlert> => {
  const response = await API.post(`/weather/alerts/`, data);
  return response.data;
};

const updateAlertMutation = async (
  data: ServerMutationAlertData,
): Promise<WeatherAlert> => {
  const response = await API.put(`/weather/alerts/${data.id}`, data);
  return response.data;
};

const deleteAlertMutation = async (data: DeleteAlertData): Promise<void> => {
  await API.delete(`/weather/alerts/${data.id}`, {
    params: { pushToken: data.pushToken },
  });
};

export const useWeatherAlert = (
  props: UseWeatherAlertProps,
): UseWeatherAlert => {
  const { alertId } = props;
  const queryClient = useQueryClient();
  const pushToken = getPushTokenFromLocalStorage();
  const createMutation = useMutation({ mutationFn: createAlertMutation });
  const updateMutation = useMutation({ mutationFn: updateAlertMutation });
  const deleteMutation = useMutation({ mutationFn: deleteAlertMutation });

  const { data, ...results } = useQuery({
    queryKey: [WEATHER_ALERT_CACHE_KEY, pushToken, alertId],
    queryFn: () => fetchAlert(alertId),
    staleTime: WEATHER_ALERT_CACHE_TTL,
    enabled: Boolean(alertId),
  });

  const saveAlert = async (alertData: SaveAlertData): Promise<WeatherAlert> => {
    const mutationData = { ...alertData, pushToken };
    if (alertId) {
      const response = await updateMutation.mutateAsync(mutationData);
      await persistedQueryClient.invalidateQueries({
        queryKey: [WEATHER_ALERTS_LIST_CACHE_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [WEATHER_ALERT_CACHE_KEY, pushToken, alertId],
      });
      return response;
    }

    const response = await createMutation.mutateAsync(mutationData);
    await persistedQueryClient.invalidateQueries({
      queryKey: [WEATHER_ALERTS_LIST_CACHE_KEY],
    });
    return response;
  };

  const deleteAlert = async (): Promise<void> => {
    await deleteMutation.mutateAsync({ id: alertId!, pushToken });
    await persistedQueryClient.invalidateQueries({
      queryKey: [WEATHER_ALERTS_LIST_CACHE_KEY],
    });
    queryClient.removeQueries({
      queryKey: [WEATHER_ALERT_CACHE_KEY, pushToken, alertId],
    });
  };

  return {
    ...results,
    alert: data,
    saveAlert,
    deleteAlert,
  };
};
